import { useState, useEffect, useMemo, FormEvent } from 'react';
import {
    Box,
    Alert,
    Divider,
    Button,
    Stack,
    Menu,
    MenuItem,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import {
    Add as AddIcon,
    EventNote as ScheduleIcon,
    SwapVert as SwapVertIcon,
    ContentCopy as CopyIcon,
    ArrowDropDown as ArrowDropDownIcon,
    Download as DownloadIcon,
    PictureAsPdf as PdfIcon,
    DeleteSweep as DeleteSweepIcon,
    History as HistoryIcon,
} from '@mui/icons-material';
import BaseModal from '@/components/modals/BaseModal';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { close } from '@/store/uiSlice';
import { setEditScheduleItem } from '@/store/modalSlice';
import {
    useGetScheduleQuery,
    useCreateScheduleItemMutation,
    useUpdateScheduleItemMutation,
    useDeleteScheduleItemMutation,
    useClearScheduleMutation,
    useGetUserInfoQuery,
    useGetSettingsQuery,
    useGetPlanResourcesQuery,
    useGetCategoriesQuery,
    useRecordMetricsEventMutation,
} from '@/store/apiSlice';
import type { ScheduleItem } from '@/types';
import ScheduleItemList from '@/components/modals/schedule/ScheduleItemList';
import ScheduleItemDialog from '@/components/modals/schedule/ScheduleItemDialog';
import CopyScheduleDialog from '@/components/modals/schedule/CopyScheduleDialog';
import CopyTemplateSetDialog from '@/components/modals/schedule/CopyTemplateSetDialog';
import ScheduleAuditDialog from '@/components/modals/schedule/ScheduleAuditDialog';
import { useI18nContext } from '@/i18n/i18n-react';
import { replaceCategoryPlaceholders } from '@/utils/schedulePlaceholders';
import { useAppDialog } from '@/components/dialogs/AppDialog';

interface PdfOptions {
    showHighlights: boolean;
    showNotes: boolean;
    showDuration: boolean;
    biggerFooter: boolean;
}

interface CheckboxMenuItemProps {
    checked: boolean;
    label: string;
    onClick: () => void;
}

const CheckboxMenuItem = ({ checked, label, onClick }: CheckboxMenuItemProps) => (
    <MenuItem onClick={onClick} sx={{ py: 0.5 }}>
        <FormControlLabel
            control={<Checkbox checked={checked} size="small" />}
            label={label}
            sx={{ m: 0, pointerEvents: 'none' }}
            slotProps={{
                typography: { sx: { fontSize: '0.875rem' } },
            }}
        />
    </MenuItem>
);

const ScheduleModal = () => {
    const dispatch = useAppDispatch();
    const { LL } = useI18nContext();
    const isOpen = useAppSelector((state) => state.ui.open.schedule);
    const editItem = useAppSelector((state) => state.modal.editScheduleItem);
    const date = useAppSelector((state) => state.modal.scheduleDate);
    const { data: userInfo } = useGetUserInfoQuery();
    const { data: settings } = useGetSettingsQuery();
    const { data: categories = [] } = useGetCategoriesQuery();

    const isHistorical = useMemo(() => {
        if (!date) return false;
        const scheduleDate = new Date(date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        scheduleDate.setHours(0, 0, 0, 0);
        return scheduleDate < today;
    }, [date]);

    const { data: planData } = useGetPlanResourcesQuery(
        { forecast: 15, includeHistory: isHistorical ? date : false },
        { skip: !date || !isOpen }
    );

    const eventData = useMemo(() => {
        return planData?.find((event) => event.date === date);
    }, [planData, date]);

    const { data: scheduleItems = [], isLoading } = useGetScheduleQuery(
        { date },
        { skip: !date || !isOpen }
    );

    const [createScheduleItem, { isLoading: isCreating }] = useCreateScheduleItemMutation();
    const [updateScheduleItem, { isLoading: isUpdating }] = useUpdateScheduleItemMutation();
    const [deleteScheduleItem, { isLoading: isDeleting }] = useDeleteScheduleItemMutation();
    const [clearSchedule, { isLoading: isClearing }] = useClearScheduleMutation();
    const [recordMetricsEvent] = useRecordMetricsEventMutation();

    const [formData, setFormData] = useState<Omit<ScheduleItem, 'id'>>({
        eventDate: date,
        title: '',
        duration: undefined,
        responsible: '',
        note: '',
        tags: '',
        position: 0,
    });

    const [error, setError] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [orderedItems, setOrderedItems] = useState<ScheduleItem[]>([]);
    const [isUpdatingPositions, setIsUpdatingPositions] = useState(false);
    const [reorderMode, setReorderMode] = useState(false);
    const [showCopyDialog, setShowCopyDialog] = useState(false);
    const [showCopyTemplateSetDialog, setShowCopyTemplateSetDialog] = useState(false);
    const [showAuditDialog, setShowAuditDialog] = useState(false);
    const [pdfMenuAnchor, setPdfMenuAnchor] = useState<null | HTMLElement>(null);
    const [pdfOptions, setPdfOptions] = useState<PdfOptions>(() => {
        try {
            const saved = localStorage.getItem('schedulePdfOptions');
            if (saved) {
                return {
                    showHighlights: true,
                    showNotes: true,
                    showDuration: true,
                    biggerFooter: true,
                    ...JSON.parse(saved),
                };
            }
        } catch {
            console.warn('Failed to parse PDF options from localStorage');
        }

        return { showHighlights: true, showNotes: true, showDuration: true, biggerFooter: true };
    });
    const [highlights, setHighlights] = useState<
        Record<number, { title?: boolean; responsible?: boolean; note?: boolean }>
    >({});

    // Find the most recent updatedAt date
    const lastEditedDate = scheduleItems.reduce(
        (latest, item) => {
            if (!item.updatedAt) return latest;
            const itemDate = new Date(item.updatedAt);
            return !latest || itemDate > latest ? itemDate : latest;
        },
        null as Date | null
    );

    const lastEditedString = lastEditedDate
        ? lastEditedDate.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
          }) +
          ' ' +
          lastEditedDate.toLocaleTimeString(undefined, {
              hour: '2-digit',
              minute: '2-digit',
          })
        : undefined;

    const itemsWithTimes = useMemo<
        (ScheduleItem & { displayStartTime: string; displayEndTime: string })[]
    >(() => {
        const startTime = eventData?.startTime || settings?.schedule?.defaultStartTime;

        if (!startTime) {
            return orderedItems.map((item) => ({
                ...item,
                title: replaceCategoryPlaceholders(item.title || '', categories, eventData),
                responsible: replaceCategoryPlaceholders(
                    item.responsible || '',
                    categories,
                    eventData
                ),
                note: replaceCategoryPlaceholders(item.note || '', categories, eventData),
                displayStartTime: '',
                displayEndTime: '',
                highlightTitle: highlights[item.id!]?.title,
                highlightResponsible: highlights[item.id!]?.responsible,
                highlightNote: highlights[item.id!]?.note,
            }));
        }

        const [hours, minutes] = startTime.split(':').map(Number);
        let currentMinutes = hours * 60 + minutes;

        return orderedItems.map((item) => {
            const startMinutes = currentMinutes;
            const endMinutes = currentMinutes + (item.duration ?? 0);
            currentMinutes = endMinutes;

            return {
                ...item,
                title: replaceCategoryPlaceholders(item.title || '', categories, eventData),
                responsible: replaceCategoryPlaceholders(
                    item.responsible || '',
                    categories,
                    eventData
                ),
                note: replaceCategoryPlaceholders(item.note || '', categories, eventData),
                displayStartTime: `${String(Math.floor(startMinutes / 60)).padStart(2, '0')}:${String(startMinutes % 60).padStart(2, '0')}`,
                displayEndTime: `${String(Math.floor(endMinutes / 60)).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`,
                highlightTitle: highlights[item.id!]?.title,
                highlightResponsible: highlights[item.id!]?.responsible,
                highlightNote: highlights[item.id!]?.note,
            };
        });
    }, [orderedItems, eventData, settings, highlights, categories]);

    useEffect(() => {
        localStorage.setItem('schedulePdfOptions', JSON.stringify(pdfOptions));
    }, [pdfOptions]);

    useEffect(() => {
        if (!isOpen || isUpdatingPositions) {
            return;
        }

        const sorted = [...scheduleItems].sort((a, b) => {
            if (a.position !== b.position) {
                return (a.position ?? 0) - (b.position ?? 0);
            }
            return (a.id ?? 0) - (b.id ?? 0);
        });

        setOrderedItems(sorted);
    }, [scheduleItems, isUpdatingPositions, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        if (editItem) {
            // Find the original item from scheduleItems to get unresolved placeholders
            const originalItem = scheduleItems.find((item) => item.id === editItem.id);
            const itemToEdit = originalItem || editItem;

            setFormData({
                eventDate: itemToEdit.eventDate,
                title: itemToEdit.title,
                duration: itemToEdit.duration,
                responsible: itemToEdit.responsible ?? '',
                note: itemToEdit.note ?? '',
                tags: itemToEdit.tags ?? '',
                position: itemToEdit.position ?? 0,
            });
            setShowForm(true);
        } else {
            setFormData({
                eventDate: date,
                title: '',
                duration: undefined,
                responsible: '',
                note: '',
                tags: '',
                position: scheduleItems.length,
            });
        }
    }, [editItem, date, scheduleItems, isOpen]);

    const canEdit = !!(userInfo?.admin || userInfo?.scheduleEditor);
    const isAdmin = !!userInfo?.admin;

    const handleClose = () => {
        dispatch(close('schedule'));
        dispatch(setEditScheduleItem(null));
        setError(null);
        setShowForm(false);
        setReorderMode(false);
        setShowCopyDialog(false);
        setShowCopyTemplateSetDialog(false);
        setShowAuditDialog(false);
        setFormData({
            eventDate: date,
            title: '',
            duration: undefined,
            responsible: '',
            note: '',
            tags: '',
            position: scheduleItems.length,
        });
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!canEdit) {
            setError(LL.errors.noPermissionEditSchedule());
            return;
        }

        try {
            if (editItem?.id) {
                await updateScheduleItem({
                    id: editItem.id,
                    ...formData,
                }).unwrap();
            } else {
                await createScheduleItem(formData).unwrap();
            }

            dispatch(setEditScheduleItem(null));
            setShowForm(false);

            setFormData({
                eventDate: date,
                title: '',
                duration: undefined,
                responsible: '',
                note: '',
                tags: '',
                position: scheduleItems.length + 1,
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : LL.errors.failedToSaveScheduleItem());
        }
    };

    const handleEdit = (item: ScheduleItem) => {
        dispatch(setEditScheduleItem(item));
        setShowForm(true);
    };
    const { confirm } = useAppDialog();
    const handleDelete = async (id: number) => {
        if (await confirm(LL.errors.deleteScheduleItemConfirm())) {
            try {
                await deleteScheduleItem(id).unwrap();
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : LL.errors.failedToDeleteScheduleItem()
                );
            }
        }
    };

    const handleClearAll = async () => {
        if (
            !(await confirm(LL.scheduleModal.clearAllConfirm(), {
                title: LL.scheduleModal.clearAllTitle(),
                confirmText: LL.scheduleModal.clearAllConfirmButton(),
                cancelText: LL.common.cancel(),
            }))
        ) {
            return;
        }

        try {
            await clearSchedule({ date }).unwrap();
        } catch (err) {
            setError(err instanceof Error ? err.message : LL.scheduleModal.clearAllError());
        }
    };

    const handleCancel = () => {
        dispatch(setEditScheduleItem(null));
        setShowForm(false);
        setFormData({
            eventDate: date,
            title: '',
            duration: undefined,
            responsible: '',
            note: '',
            tags: '',
            position: scheduleItems.length,
        });
        setError(null);
    };

    const handleReorder = async (activeId: number, overId: number) => {
        const oldIndex = orderedItems.findIndex((item) => item.id === activeId);
        const newIndex = orderedItems.findIndex((item) => item.id === overId);

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        const newItems = [...orderedItems];
        const [movedItem] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, movedItem);

        setOrderedItems(newItems);
        setIsUpdatingPositions(true);

        try {
            const updates = newItems.map((item, index) => {
                if (item.position !== index) {
                    return updateScheduleItem({
                        ...item,
                        position: index,
                    }).unwrap();
                }
                return Promise.resolve();
            });
            await Promise.all(updates);
        } catch (err) {
            setError(err instanceof Error ? err.message : LL.errors.failedToReorderItems());
        } finally {
            setTimeout(() => setIsUpdatingPositions(false), 100);
        }
    };

    const handleToggleHighlight = (itemId: number, field: 'title' | 'responsible' | 'note') => {
        setHighlights((prev) => ({
            ...prev,
            [itemId]: {
                ...prev[itemId],
                [field]: !prev[itemId]?.[field],
            },
        }));
    };

    const handleDownloadPdf = async () => {
        if (itemsWithTimes.length === 0) {
            return;
        }

        setPdfMenuAnchor(null);

        const eventDate = new Date(date).toLocaleDateString(undefined, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });

        // Filter out highlights and/or notes based on options
        const itemsForPdf = itemsWithTimes.map((item) => ({
            ...item,
            note: pdfOptions.showNotes ? item.note : '',
            highlightTitle: pdfOptions.showHighlights ? item.highlightTitle : false,
            highlightResponsible: pdfOptions.showHighlights ? item.highlightResponsible : false,
            highlightNote:
                pdfOptions.showHighlights && pdfOptions.showNotes ? item.highlightNote : false,
        }));

        const { generateSchedulePdf } = await import('@/utils/schedulePdf');
        generateSchedulePdf({
            eventName: eventData?.event || LL.scheduleModal.pdfTitle(),
            eventLocation: eventData?.location || LL.scheduleModal.pdfLocation(),
            eventDate,
            isoDate: date,
            filenameTemplate: settings?.schedule?.pdfFilename,
            eventTime: (eventData?.startTime ?? settings?.schedule?.defaultStartTime)?.substring(
                0,
                5
            ),
            lastEdited: lastEditedString,
            items: itemsForPdf,
            showDuration: pdfOptions.showDuration,
            biggerFooter: pdfOptions.biggerFooter,
            translations: {
                title: LL.scheduleModal.pdfTitle(),
                topic: LL.scheduleModal.pdfTopic(),
                duration: LL.scheduleModal.pdfDuration(),
                responsible: LL.scheduleModal.pdfResponsible(),
                note: LL.scheduleModal.pdfNote(),
                time: LL.scheduleModal.pdfTime(),
                minutes: LL.scheduleModal.pdfMinutes(),
                lastEdited: LL.scheduleModal.lastEdited(),
                categoryFormat: LL.scheduleModal.categoryFormat({
                    category: '{category}',
                    responsible: '{responsible}',
                }),
                noAssignment: LL.scheduleModal.noAssignment(),
            },
            footerConfig: settings?.schedule?.footer,
            categories: categories.map((cat) => ({
                name: cat.name,
                responsible:
                    eventData?.categories?.[cat.id]?.users
                        ?.map((u) => u.name)
                        .filter(Boolean)
                        .join(', ') || undefined,
            })),
        });

        // Record PDF creation metric (fire-and-forget)
        const activeOptions = Object.entries(pdfOptions)
            .filter(([, v]) => v === true)
            .map(([k]) => k);
        recordMetricsEvent({ eventType: 'pdf_created', meta: { options: activeOptions } });
    };

    const formatTime = (time?: string) => {
        if (!time) return '';
        return time.substring(0, 5);
    };

    if (!isOpen) {
        return null;
    }

    return (
        <BaseModal
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            styledHeader={{
                icon: <ScheduleIcon />,
                title: LL.scheduleModal.title(),
                subtitle: [
                    `${new Date(date).toLocaleDateString(undefined, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                    })} - ${eventData?.startTime?.substring(0, 5) ?? settings?.schedule?.defaultStartTime}`,
                    `${lastEditedString ? `${LL.scheduleModal.lastEdited()}: ${lastEditedString}` : ''}`,
                ],
                gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
            loading={isLoading}
        >
            <Box sx={{ p: { sm: 0, md: 2 } }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {!eventData?.startTime && !settings?.schedule?.defaultStartTime && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {LL.scheduleModal.noStartTimeWarning()}
                    </Alert>
                )}

                <ScheduleItemList
                    items={itemsWithTimes}
                    canEdit={canEdit}
                    reorderEnabled={reorderMode}
                    editItemId={editItem?.id ?? null}
                    isDeleting={isDeleting}
                    categories={categories}
                    eventData={eventData}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onReorder={handleReorder}
                    onToggleHighlight={handleToggleHighlight}
                    formatTime={formatTime}
                />
                {canEdit && scheduleItems.length > 0 && <Divider sx={{ my: { xs: 2, sm: 3 } }} />}

                <Stack
                    direction="row"
                    spacing={2}
                    sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                    useFlexGap={true}
                >
                    {canEdit && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => setShowForm(true)}
                            sx={{
                                fontSize: { xs: '0.7125rem', sm: '0.875rem' },
                            }}
                        >
                            {LL.scheduleModal.addTopic()}
                        </Button>
                    )}

                    {canEdit && scheduleItems.length === 0 && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CopyIcon />}
                            onClick={() => setShowCopyDialog(true)}
                            sx={{
                                fontSize: { xs: '0.7125rem', sm: '0.875rem' },
                            }}
                        >
                            {LL.scheduleModal.copyFromPrevious()}
                        </Button>
                    )}

                    {canEdit && scheduleItems.length === 0 && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ScheduleIcon />}
                            onClick={() => setShowCopyTemplateSetDialog(true)}
                            sx={{
                                fontSize: { xs: '0.7125rem', sm: '0.875rem' },
                            }}
                        >
                            {LL.scheduleModal.copyFromTemplateSet()}
                        </Button>
                    )}

                    {canEdit && scheduleItems.length > 1 && (
                        <Button
                            variant={reorderMode ? 'contained' : 'outlined'}
                            size="small"
                            startIcon={<SwapVertIcon sx={{ fontSize: { sm: 18, md: 20 } }} />}
                            onClick={() => setReorderMode(!reorderMode)}
                            sx={{
                                fontSize: { sm: '0.7125rem', md: '0.875rem' },
                            }}
                        >
                            {reorderMode
                                ? LL.scheduleModal.doneReordering()
                                : LL.scheduleModal.reorder()}
                        </Button>
                    )}

                    {isAdmin && scheduleItems.length > 0 && (
                        <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            startIcon={<DeleteSweepIcon sx={{ fontSize: { sm: 18, md: 20 } }} />}
                            onClick={handleClearAll}
                            disabled={isClearing}
                            sx={{
                                fontSize: { sm: '0.7125rem', md: '0.875rem' },
                            }}
                        >
                            {LL.scheduleModal.clearAll()}
                        </Button>
                    )}

                    {isAdmin && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<HistoryIcon sx={{ fontSize: { sm: 18, md: 20 } }} />}
                            onClick={() => setShowAuditDialog(true)}
                            sx={{
                                fontSize: { sm: '0.7125rem', md: '0.875rem' },
                            }}
                        >
                            {LL.scheduleModal.changeHistory()}
                        </Button>
                    )}

                    {scheduleItems.length > 0 && (
                        <>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon sx={{ fontSize: { sm: 18, md: 20 } }} />}
                                endIcon={
                                    <ArrowDropDownIcon sx={{ fontSize: { sm: 18, md: 20 } }} />
                                }
                                onClick={(e) => setPdfMenuAnchor(e.currentTarget)}
                                sx={{
                                    fontSize: { sm: '0.7125rem', md: '0.875rem' },
                                    ml: 'auto',
                                }}
                            >
                                {LL.scheduleModal.download()}
                            </Button>
                            <Menu
                                anchorEl={pdfMenuAnchor}
                                open={Boolean(pdfMenuAnchor)}
                                onClose={() => setPdfMenuAnchor(null)}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                <CheckboxMenuItem
                                    checked={pdfOptions.showHighlights}
                                    label={LL.scheduleModal.showHighlights()}
                                    onClick={() =>
                                        setPdfOptions((prev) => ({
                                            ...prev,
                                            showHighlights: !prev.showHighlights,
                                        }))
                                    }
                                />
                                <CheckboxMenuItem
                                    checked={pdfOptions.showNotes}
                                    label={LL.scheduleModal.showNotes()}
                                    onClick={() =>
                                        setPdfOptions((prev) => ({
                                            ...prev,
                                            showNotes: !prev.showNotes,
                                        }))
                                    }
                                />
                                <CheckboxMenuItem
                                    checked={pdfOptions.showDuration}
                                    label={LL.scheduleModal.showDuration()}
                                    onClick={() =>
                                        setPdfOptions((prev) => ({
                                            ...prev,
                                            showDuration: !prev.showDuration,
                                        }))
                                    }
                                />
                                <CheckboxMenuItem
                                    checked={pdfOptions.biggerFooter}
                                    label={LL.scheduleModal.biggerFooter()}
                                    onClick={() =>
                                        setPdfOptions((prev) => ({
                                            ...prev,
                                            biggerFooter: !prev.biggerFooter,
                                        }))
                                    }
                                />
                                <Divider />
                                <MenuItem onClick={handleDownloadPdf}>
                                    <PdfIcon sx={{ mr: 1, fontSize: 20 }} />
                                    {LL.scheduleModal.downloadPdf()}
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                </Stack>

                {canEdit && (
                    <ScheduleItemDialog
                        open={showForm}
                        editItem={editItem}
                        formData={formData}
                        isCreating={isCreating}
                        isUpdating={isUpdating}
                        onChange={setFormData}
                        onSubmit={handleSubmit}
                        onCancel={handleCancel}
                    />
                )}

                {canEdit && (
                    <CopyScheduleDialog
                        open={showCopyDialog}
                        onClose={() => setShowCopyDialog(false)}
                        targetDate={date}
                    />
                )}

                {canEdit && (
                    <CopyTemplateSetDialog
                        open={showCopyTemplateSetDialog}
                        onClose={() => setShowCopyTemplateSetDialog(false)}
                        targetDate={date}
                    />
                )}

                {isAdmin && (
                    <ScheduleAuditDialog
                        open={showAuditDialog}
                        onClose={() => setShowAuditDialog(false)}
                        date={date}
                    />
                )}
            </Box>
        </BaseModal>
    );
};

export default ScheduleModal;
